"use client";

import { useEffect, useRef, useState } from "react";
import { IconHelp } from "./icons";
import { useRestored } from "./restored";
import { t, type Lang } from "@/lib/i18n/index";

/**
 * Where to write when something is wrong (Issue 987): a ? in the top bar,
 * and under it the address (`SUPPORT_CONTACT`), as a mail link and a copy
 * button.
 *
 * It opens two ways. A mouse resting on it opens it and leaving closes it —
 * unless it was clicked, which keeps it open; a phone has no hover, so a
 * tap must open it, and does. Built like `SignOutMenu` beside it otherwise:
 * Escape and a click outside close it, and the back-forward cache brings it
 * back closed (`useRestored`).
 *
 * Only a press counts as open to the keyboard (`aria-expanded`): the agent
 * panel's own Escape stands back while a menu is open (it looks for
 * `aria-haspopup` with `aria-expanded="true"`), and a mouse that happens to
 * rest on the ? while someone types in the panel must not take Escape from
 * it. For the same reason Escape hands focus back to the ? only when it was
 * pressed open or focus is inside the box — otherwise focus was never here.
 *
 * Copying can be refused (an insecure origin, a permission): then the
 * address is selected and the copying is theirs, as in `KeyDialog`.
 */
export function HelpMenu({ lang, contact }: { lang: Lang; contact: string }) {
  const s = t(lang);
  // How it is open: by a resting mouse, which leaving undoes, or by a press.
  const [open, setOpen] = useState<"hover" | "press" | null>(null);
  const [copied, setCopied] = useState<"yes" | "byHand" | null>(null);
  const wrap = useRef<HTMLDivElement>(null);
  const icon = useRef<HTMLButtonElement>(null);
  const address = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (!open) return;
    function away(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(null);
    }
    function key(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      const ours = open === "press" || !!wrap.current?.contains(document.activeElement);
      setOpen(null);
      if (ours) icon.current?.focus();
    }
    document.addEventListener("click", away);
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("click", away);
      document.removeEventListener("keydown", key);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setCopied(null);
  }, [open]);

  useRestored(() => setOpen(null));

  function press() {
    setOpen((o) => (o === "press" ? null : "press"));
  }

  async function copy() {
    // Held open from here: a box that only a resting mouse holds would close
    // under them while they copy the address by hand.
    setOpen("press");
    try {
      await navigator.clipboard.writeText(contact);
      setCopied("yes");
    } catch {
      const range = document.createRange();
      if (address.current) range.selectNodeContents(address.current);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
      setCopied("byHand");
    }
  }

  return (
    <div
      className={"help" + (open ? " open" : "")}
      ref={wrap}
      onPointerEnter={(e) => { if (e.pointerType === "mouse") setOpen((o) => o ?? "hover"); }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") setOpen((o) => (o === "hover" ? null : o)); }}
    >
      <button
        ref={icon}
        type="button"
        className="icon-btn"
        aria-haspopup="dialog"
        data-track="shell.help_open"
        aria-expanded={open === "press"}
        aria-label={s["help.label"]}
        title={s["help.label"]}
        onClick={press}
      >
        <IconHelp />
      </button>
      <div className="help-menu" role="dialog" aria-label={s["help.label"]}>
        <p className="help-lede">{s["help.lede"]}</p>
        <div className="help-row">
          <a ref={address} className="help-mail" href={`mailto:${contact}`} data-track="shell.help_mail">{contact}</a>
          <button type="button" className="btn sm" data-track="shell.help_copy" onClick={copy}>
            {copied === "yes" ? s["help.copied"] : s["help.copy"]}
          </button>
        </div>
        {copied === "byHand" ? <p className="help-note" role="status">{s["help.copyByHand"]}</p> : null}
      </div>
    </div>
  );
}
